import { useBoardFromContext } from "../../../utils/hooks/commonly-stored/uploaded-board/board";
import {
  Board,
  BoardContext,
  FabDrawingsStatus,
  ImageSet,
} from "../../../reducers/uploadedBoard";
import SVGViewer from "../../common/SVGViewer";
import Modal from "../../common/Modal";
import ModalContentWithCloseButton from "../../common/ModalContentWithCloseButton";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";
import LoadingIcon from "../../common/LoadingIcon";
import {
  DownloadableFile,
  FileLink,
  HorizontalContainer,
  PreviewImg,
  Remove,
  Subtitle,
  Title,
  VerticalContainer,
} from "./AssemblyDrawingsStyles";
import Button from "../../common/Button";
import useFetchAndTrace from "../../../utils/hooks/fetchWithTrace";
import { useCustomAlert, useCustomConfirm } from "../../../utils/hooks/alerts";
import settings from "../../../constants/settings";
import LoadingOverlay from "../../LoadingOverlay";
import { ButtonVariants } from "../../common/Button/constants";
import useSendBoardEvent, {
  BoardTransitionEvent,
  MachineGroup,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/sendBoardEvent";
import { useAppSelector } from "../../../utils/hooks/store";
import useGetCustomerImageFiles, {
  CustomerImageFilesResponse,
} from "../../../utils/hooks/commonly-stored/uploaded-board/getCustomerImageFiles";
import Icon from "../../common/Icon";
import { DownloadableFileResponse } from "../../../utils/hooks/uploadToS3";
import useCustomerImageFiles from "../../../utils/hooks/commonly-stored/uploaded-board/customerImageFiles";
import { OneLineMissingPlacements } from "../PickAndPlace/MissingPlacements";
import UploadCustomerImageFiles from "../../UploadBoard/AdditionalImageFilesUpload";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";

export function useDownloadAssemblyDrawings() {
  const fetchAndTrace = useFetchAndTrace();
  const customAlert = useCustomAlert();

  const errorHandler = () => {
    customAlert("Failed to download assembly drawings");
  };

  return (boardId: string, boardProjectName: string) => {
    const getAssemblyDrawingsHandler = async (response: Response) => {
      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement("a");
        link.download = `${boardProjectName}-${boardId}-assembly-drawings.pdf`;
        link.href = URL.createObjectURL(blob);
        link.click();
      } else {
        throw Error("Failed to get assembly drawings");
      }
    };

    void fetchAndTrace(
      `${settings.api_url}/boards/${boardId}/assembly-pdf`,
      getAssemblyDrawingsHandler,
      errorHandler,
      {
        method: "PUT",
      },
    );
  };
}

function AssemblyPreview({
  assemblyImage,
  setSelectedAssembly,
}: {
  assemblyImage: ImageSet;
  setSelectedAssembly: Dispatch<SetStateAction<ImageSet | null>>;
}) {
  const selectAssembly = () => {
    setSelectedAssembly(assemblyImage);
  };

  return (
    <VerticalContainer>
      <PreviewImg
        src={assemblyImage.preview as string}
        alt={assemblyImage.name as string}
        onClick={selectAssembly}
      />
      <div>{assemblyImage.name}</div>
      <Button label={"Open interactive view"} onClick={selectAssembly} />
    </VerticalContainer>
  );
}

function CreateFabDrawings({ recreate }: { recreate?: boolean }) {
  const board = useBoardFromContext().board as Board;

  const customAlert = useCustomAlert();

  const errorHandler = useCallback(() => {
    customAlert("Failed to create fab drawings");
  }, [customAlert]);

  const { sendBoardEvent, loading } = useSendBoardEvent(errorHandler);

  const createFabDrawings = () => {
    void sendBoardEvent(board.id, {
      machine_group: MachineGroup.BOARD,
      model_id: board.id,
      event: BoardTransitionEvent.SHOULD_CREATE_FAB_DRAWINGS,
    });
  };

  return (
    <>
      <LoadingOverlay
        loading={loading}
        text={"Kicking off fab drawing creation..."}
      />
      <Button
        label={recreate ? "Recreate fab drawings" : "Create fab drawings"}
        onClick={createFabDrawings}
        variant={recreate ? ButtonVariants.SECONDARY : ButtonVariants.PRIMARY}
      />
    </>
  );
}

function noFilesUploaded(customerFilesResponse: CustomerImageFilesResponse) {
  return (
    customerFilesResponse.assembly_drawings.length == 0 &&
    customerFilesResponse.fab_drawings.length == 0 &&
    customerFilesResponse.reference_files.length == 0
  );
}

function FileRow({ attachment }: { attachment: DownloadableFileResponse }) {
  const { removeCustomerImageFile, loading } = useCustomerImageFiles();
  const customConfirm = useCustomConfirm();

  const handleRemove = () => {
    customConfirm(`Remove ${attachment.file_name}?`, () => {
      removeCustomerImageFile(attachment);
    });
  };

  return (
    <>
      <LoadingOverlay loading={loading} text={"Updating files..."} />
      <DownloadableFile key={attachment.link}>
        <Icon src={"/icons/file_gray.svg"} alt={"File"} />
        <FileLink href={attachment.link}>{attachment.file_name}</FileLink>
        <Remove onClick={handleRemove}>
          <Icon src={"/icons/cancel.svg"} alt={"File"} iconDimension={20} />
        </Remove>
      </DownloadableFile>
    </>
  );
}

function FileGroup({
  group,
  name,
}: {
  group: DownloadableFileResponse[];
  name: string;
}) {
  if (group.length > 0) {
    return (
      <>
        <Subtitle>{name}</Subtitle>
        {group.map((attachment) => {
          return <FileRow key={attachment.link} attachment={attachment} />;
        })}
      </>
    );
  }
}

export function CustomerUploadedFiles() {
  const { customerFilesResponse } = useGetCustomerImageFiles();

  if (!customerFilesResponse || noFilesUploaded(customerFilesResponse)) {
    return null;
  }

  return (
    <>
      <FileGroup
        group={customerFilesResponse.assembly_drawings}
        name={"Assembly drawings"}
      />
      <FileGroup
        group={customerFilesResponse.fab_drawings}
        name={"Fab drawings"}
      />
      <FileGroup
        group={customerFilesResponse.reference_files}
        name={"Reference files"}
      />
    </>
  );
}

function FlashPCBAssemblyDrawings() {
  const board = useBoardFromContext().board as Board;
  const downloadAssemblyDrawing = useDownloadAssemblyDrawings();
  const [selectedAssembly, setSelectedAssembly] = useState<ImageSet | null>(
    null,
  );
  const images = useAppSelector(
    (state) => (state.uploadedBoard.boards[board.id] as BoardContext).images,
  );

  const openAsPDF = () => {
    downloadAssemblyDrawing(board.id, board.project_name);
  };

  if (images === null) {
    return <LoadingIcon text={"Loading assembly drawings..."} />;
  }

  if (images.fab_drawings.status == FabDrawingsStatus.PROCESSING) {
    return <LoadingIcon text={"Generating assembly drawings..."} />;
  }

  if (
    images.fab_drawings.status == FabDrawingsStatus.DRAWN &&
    images.fab_drawings.drawings.length > 0
  ) {
    return (
      <VerticalContainer gap={"3rem"}>
        {images.fab_drawings.drawings.map((drawing) => {
          const drawing_name = drawing.part_placement_type;
          return (
            <VerticalContainer key={drawing_name}>
              <Title>{drawing_name}</Title>

              <HorizontalContainer>
                {drawing.top_assembly !== null && (
                  <AssemblyPreview
                    assemblyImage={drawing.top_assembly}
                    setSelectedAssembly={setSelectedAssembly}
                  />
                )}
                {drawing.bottom_assembly !== null && (
                  <AssemblyPreview
                    assemblyImage={drawing.bottom_assembly}
                    setSelectedAssembly={setSelectedAssembly}
                  />
                )}
              </HorizontalContainer>
            </VerticalContainer>
          );
        })}
        <HorizontalContainer>
          <CreateFabDrawings recreate />
          <Button label={"Open as PDF"} onClick={openAsPDF} />
        </HorizontalContainer>
        <Modal
          isOpen={selectedAssembly !== null}
          onRequestClose={() => setSelectedAssembly(null)}
        >
          <ModalContentWithCloseButton
            closeModal={() => setSelectedAssembly(null)}
            showCloseHint
            fullScreen
            lessPadding
            title={`${board.project_name} ${selectedAssembly?.name} assembly`}
          >
            {selectedAssembly !== null && (
              <SVGViewer
                svgInput={{ svgUrl: selectedAssembly.svg as string }}
              />
            )}
          </ModalContentWithCloseButton>
        </Modal>
      </VerticalContainer>
    );
  }
  return <CreateFabDrawings />;
}

export default function AssemblyDrawings() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const [moreAssemblyFiles, setMoreAssemblyFiles] = useState(false);

  const closeUploadMoreAssemblyFilesModal = () => setMoreAssemblyFiles(false);

  return (
    <>
      <OneLineMissingPlacements />
      <Button
        label={"Upload additional drawings / files"}
        onClick={() => setMoreAssemblyFiles(true)}
      />
      <CustomerUploadedFiles />
      <FlashPCBAssemblyDrawings />
      <Modal
        isOpen={moreAssemblyFiles}
        onRequestClose={closeUploadMoreAssemblyFilesModal}
      >
        <ModalContentWithCloseButton
          title={"Upload image files"}
          closeModal={closeUploadMoreAssemblyFilesModal}
        >
          <UploadCustomerImageFiles
            closeModal={closeUploadMoreAssemblyFilesModal}
            boardId={boardId}
          />
        </ModalContentWithCloseButton>
      </Modal>
    </>
  );
}
